import React from "react";
import { Box, ButtonBase, Stack } from "@mui/material";

export interface ThumbnailItem {
    id: string;
    src: string;
    alt: string;
}

interface ImageThumbnailStripProps {
    images: ThumbnailItem[];
    onOpen: (index: number) => void;
    /** Thumbnail edge in px. */
    size?: number;
    /** Optional buttons under each thumbnail. */
    renderActions?: (image: ThumbnailItem, index: number) => React.ReactNode;
}

/**
 * A row of square thumbnails that open full size. Wraps on wider screens;
 * on phones it becomes one horizontally swipeable row so a long set never
 * pushes the content down.
 */
export const ImageThumbnailStrip: React.FC<ImageThumbnailStripProps> = ({
    images,
    onOpen,
    size = 88,
    renderActions,
}) => {
    if (images.length === 0) return null;
    return (
        <Stack
            direction="row"
            spacing={1.25}
            useFlexGap
            sx={{
                flexWrap: { xs: "nowrap", sm: "wrap" },
                overflowX: { xs: "auto", sm: "visible" },
                pb: { xs: 0.5, sm: 0 },
                scrollbarWidth: "thin",
                "& > *": { flexShrink: 0 },
            }}
        >
            {images.map((image, index) => (
                <Stack key={image.id} spacing={0.5} alignItems="center" sx={{ width: size }}>
                    <ButtonBase
                        onClick={() => onOpen(index)}
                        aria-label={`Open image ${index + 1} of ${images.length} full size`}
                        focusRipple
                        sx={{
                            width: size,
                            height: size,
                            borderRadius: 2,
                            overflow: "hidden",
                            border: 1,
                            borderColor: "divider",
                            bgcolor: "grey.100",
                            "&:hover img, &.Mui-focusVisible img": { transform: "scale(1.04)" },
                            "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
                        }}
                    >
                        <Box
                            component="img"
                            src={image.src}
                            alt={image.alt}
                            loading="lazy"
                            draggable={false}
                            sx={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 150ms" }}
                        />
                    </ButtonBase>
                    {renderActions && (
                        <Stack direction="row" spacing={0.25} justifyContent="center">
                            {renderActions(image, index)}
                        </Stack>
                    )}
                </Stack>
            ))}
        </Stack>
    );
};
